/**
 * Voice-message moderation (Phase 2, AMB) — web only.
 *
 * Voice recording is a web-only feature in this app. The Stream upload seam
 * (`attachmentManager.setCustomUploadFn`) intercepts EVERY attachment upload,
 * so this module wraps it: audio clips are first POSTed to the awesome-chat-api
 * voice-moderation endpoint (`POST /api/v2/voice/moderate`, which transcribes
 * with Whisper then runs omni-moderation on the transcript). A block-class
 * verdict aborts the upload — the message never sends, so no recipient ever
 * sees it. A clean (or merely flagged) clip, and every non-audio attachment,
 * falls through to Stream's own default upload (Stream's native engine
 * moderates text/images server-side; only audio is genuinely ours to gate).
 *
 * The factory is dependency-injected so the decision logic is unit-testable
 * without the SDK or the network; the thin wiring in ChatsScreen supplies the
 * real deps (Stream's default upload + the file-like accessors).
 */

import { chatApiFetch } from './chat/chatApiFetch';

export interface VoiceModerationResult {
  approved: boolean;
  action: string;
  reason: string | null;
}

/**
 * User-facing block notice (Russian — brand voice: calm, no exclamation, not
 * bot-like). The backend's English `reason` is for the admin store/logs, not
 * for display.
 */
export const VOICE_BLOCKED_MESSAGE =
  'Это голосовое сообщение не прошло проверку — так у нас отправлять нельзя.';

/**
 * CORE-196 — проверить не удалось. Отдельный текст: человек ничего не
 * нарушал, запись у него на руках, ему надо предложить повторить.
 */
export const VOICE_UNAVAILABLE_MESSAGE =
  'Не удалось проверить запись — попробуйте отправить ещё раз.';

const AUDIO_MIME_PREFIX = 'audio/';

/** True for `audio/*` MIME types (case-insensitive). */
export function isAudioMimeType(mimeType: string | null | undefined): boolean {
  return !!mimeType && mimeType.toLowerCase().startsWith(AUDIO_MIME_PREFIX);
}

/**
 * POST a voice clip to the moderation endpoint and return the verdict.
 *
 * CORE-196 — НЕ пропускает при сбое. Раньше любая транспортная ошибка и любой
 * не-2xx одобряли запись: голос уходил непроверенным, а следом был только
 * console.warn. Оправданием служило то, что бэкенд и сам пропускал при сбое
 * провайдера — теперь не пропускает, и здесь тоже.
 *
 * Голос — самая неудобная поверхность для «пропустим на всякий случай»:
 * расшифровку читает только модерация, и никто в чате не заметит, что запись
 * не проверяли.
 */
export async function moderateVoiceClip(
  blob: Blob,
  mimeType: string,
  durationSeconds?: number,
): Promise<VoiceModerationResult> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': mimeType || 'application/octet-stream',
    };
    if (typeof durationSeconds === 'number' && Number.isFinite(durationSeconds)) {
      headers['X-Audio-Seconds'] = String(Math.round(durationSeconds));
    }

    const res = await chatApiFetch('/api/v2/voice/moderate', {
      method: 'POST',
      headers,
      body: blob,
    });

    if (!res.ok) {
      console.warn(`Voice moderation returned ${res.status} — запись не пропускаем`);
      return { approved: false, action: 'error', reason: null };
    }

    const data = await res.json();
    return {
      approved: data.approved !== false,
      action: data.action ?? 'allow',
      reason: data.reason ?? null,
    };
  } catch (err) {
    console.warn('Voice moderation request failed — запись не пропускаем:', err);
    return { approved: false, action: 'error', reason: null };
  }
}

export type UploadResult = { file: string; thumb_url?: string } & Record<string, unknown>;

export interface ModeratedUploadDeps {
  /** Stream's default upload for this file-like (sendImage / sendFile). */
  defaultUpload: (fileLike: any) => Promise<UploadResult>;
  /** MIME type of the file-like (`File`/`Blob` `.type`, or `FileReference.type`). */
  getMimeType: (fileLike: any) => string;
  /** Raw bytes for the moderation POST. */
  getBlob: (fileLike: any) => Promise<Blob>;
  /** Clip duration in seconds, if known (e.g. `FileReference.duration`). */
  getDurationSeconds?: (fileLike: any) => number | undefined;
  /** Moderation call — injectable for tests; defaults to `moderateVoiceClip`. */
  moderate?: (
    blob: Blob,
    mimeType: string,
    durationSeconds?: number,
  ) => Promise<VoiceModerationResult>;
  /** Surface the block to the user (toast/alert). */
  onBlocked?: (message: string) => void;
}

/**
 * Build a Stream `UploadRequestFn`. Audio → moderate first (block ⇒ throw,
 * which aborts the send before any recipient sees it); everything else, and
 * any approved clip, → Stream's default upload.
 */
export function createModeratedUploadFn(
  deps: ModeratedUploadDeps,
): (fileLike: any) => Promise<UploadResult> {
  const moderate = deps.moderate ?? moderateVoiceClip;

  return async (fileLike: any): Promise<UploadResult> => {
    const mimeType = deps.getMimeType(fileLike) || '';

    // Non-audio falls through untouched — Stream moderates those itself.
    if (!isAudioMimeType(mimeType)) {
      return deps.defaultUpload(fileLike);
    }

    const blob = await deps.getBlob(fileLike);
    const durationSeconds = deps.getDurationSeconds?.(fileLike);
    const verdict = await moderate(blob, mimeType, durationSeconds);

    if (!verdict.approved) {
      // CORE-196 — «не смогли проверить» и «нарушает правила» это разные
      // сообщения. Запись человек уже наговорил, и обвинять его за наш сбой,
      // когда достаточно нажать «отправить» ещё раз, — худшее, что тут можно
      // сделать.
      const message =
        verdict.action === 'error' ? VOICE_UNAVAILABLE_MESSAGE : VOICE_BLOCKED_MESSAGE;
      deps.onBlocked?.(message);
      // Throwing rejects the upload promise → Stream marks the attachment
      // failed and never sends the message.
      throw new Error(message);
    }

    return deps.defaultUpload(fileLike);
  };
}
